import * as notifier from './notifier.js';
import * as loader from './loader.js';
import * as user from '../xhr/user.js';
import * as session from '../services/session.js';
import * as events from '../services/events.js';
import * as types from './types/user.js';
import * as whitelabelTypes from './types/whitelabel.js';
import * as usageTypes from './types/usage.js';
import * as coverageTypes from './types/coverage.js';
import * as marketplaceTypes from './types/marketplace.js';
import * as claimsTypes from './types/claims.js';
import * as invoiceTypes from './types/invoices.js';
import * as spendingAccountsTypes from './types/spendingAccounts.js';
import { i18n, tokens } from '@simplybenefits/sb-ui-library';

/**
 * @param dispatch
 * @param isLoading
 * @param type
 */
function setLoader(dispatch, isLoading, type = 'full') {
    dispatch(loader.setLoader({
        isLoading,
        type
    }));
}

/**
 *
 * @param {*} dispatch
 * @param {*} state
 */
function setBankingDetailsVerifying(dispatch, state) {
    dispatch({
        type: types.BANKING_DETAILS_VERIFYING,
        state
    });
}

/**
 * @returns {function(*): Promise<unknown>}
 */
export function refreshToken() {
    return (dispatch) => {
        setLoader(dispatch, true);

        return user
            .refreshToken()
            .then(async (data) => {
                await session.setToken(data.token);
                window.location.href = '/';
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
                return null;
            });
    };
}

/**
 * @returns {Function}
 */
export function getLocalStorageWhitelabel() {
    return async (dispatch) => {
        const data = await session.getWhitelabel();

        if (!data) {
            return null;
        }

        dispatch({
            type: whitelabelTypes.SAVE_WHITE_LABEL,
            data
        });

        return data;
    };
}

/**
 * @param hasAuth
 * @returns {function(*=): Promise<unknown>}
 */
export function getWhiteLabel(hasAuth) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return user
            .getWhiteLabel(hasAuth)
            .then(async (data) => {
                if (!data || Object.keys(data).length === 0) {
                    if (hasAuth) {
                        await session.removeWhitelabel();
                    }

                    setLoader(dispatch, false);
                    return null;
                }

                // save the whitelabel in local storage for if they logout
                await session.saveWhitelabel(data);

                dispatch({
                    type: whitelabelTypes.SAVE_WHITE_LABEL,
                    data
                });

                setLoader(dispatch, false);

                return data;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
                return null;
            });
    };
}

/**
 * @returns {function(*=): Promise<unknown>}
 */
export function loadUserData() {
    return (dispatch) => {
        setLoader(dispatch, true);

        return user
            .get()
            .then((data) => {
                const userLanguage = data?.meta?.language;
                i18n.setLocale(userLanguage || "en");

                const storedLanguage = session.getUserLanguage();
                if (storedLanguage !== userLanguage) {
                    session.setUserLanguage(userLanguage || "en");
                }   

                session.setToken(data.token);

                const onlySpendingAccountActive = !data.meta.isMarketplaceOnly && (
                    !(data.coverage.criticalIllness.benefitActive === 1
                    || data.coverage.deathDismemberment.benefitActive === 1
                    || data.coverage.dental.benefitActive === 1
                    || data.coverage.dependentLife.benefitActive === 1
                    || data.coverage.drugs.benefitActive === 1
                    || data.coverage.efap.benefitActive === 1
                    || data.coverage.lifeInsurance.benefitActive === 1
                    || data.coverage.ltd.benefitActive === 1
                    || data.coverage.majorMedical.benefitActive === 1
                    || data.coverage.outOfCountry.benefitActive === 1
                    || data.coverage.paramedicals.benefitActive === 1
                    || data.coverage.std.benefitActive === 1
                    || data.coverage.vision.benefitActive === 1
                    || data.coverage.virtualHealthcare.benefitActive === 1));

                const noClaims = data.claims.length === 0;

                dispatch({
                    type: types.GET_USER,
                    meta: data.meta,
                    dependents: data.dependents,
                    beneficiaries: data.beneficiaries,
                    trustee: data.trustee,
                    notifications: data.notifications || [],
                    onlySpendingAccountActive,
                    noClaims,
                    planDesign: data.planDesign
                });

                if (data.notifications && data.notifications.length > 0) {
                    dispatch(notifier.displayMessage(i18n.t(tokens.labels.notifications.youHaveNewNotifications)));
                }

                dispatch({
                    type: marketplaceTypes.GET,
                    marketplace: data.marketplace
                });

                dispatch({
                    type: coverageTypes.GET_COVERAGE,
                    coverage: data.coverage
                });

                dispatch({
                    type: usageTypes.GET_USAGE,
                    usage: data.usage
                });

                dispatch({
                    type: claimsTypes.GET_CLAIMS,
                    claims: data.claims
                });

                dispatch({
                    type: spendingAccountsTypes.GET_HEALTH_USAGE,
                    health: data.spending.health
                });

                dispatch({
                    type: spendingAccountsTypes.GET_LIFESTYLE_CLAIMS,
                    lifestyle: data.spending.lifestyle,
                    lsaClaimTypes: data.spending.lsaClaimTypes
                });

                dispatch({
                    type: invoiceTypes.GET_ALL_INVOICES,
                    invoices: data.invoices
                });
            })
            .then(() => {
                setLoader(dispatch, false);
                return true;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
            });
    };
}

/**
 * @returns {function(*=): Promise<unknown>}
 */
export function loadDependentData() {
    return (dispatch) => {
        setLoader(dispatch, true);

        return user
            .getDependent()
            .then((data) => {
                const userLanguage = data?.meta?.language || session.getUserLanguage();
                i18n.setLocale(userLanguage || "en");
                session.setToken(data.token);

                const onlySpendingAccountActive = !(data.coverage.criticalIllness?.benefitActive === 1
                    || data.coverage.deathDismemberment?.benefitActive === 1
                    || data.coverage.dental?.benefitActive === 1
                    || data.coverage.dependentLife?.benefitActive === 1
                    || data.coverage.drugs?.benefitActive === 1
                    || data.coverage.efap?.benefitActive === 1
                    || data.coverage.lifeInsurance?.benefitActive === 1
                    || data.coverage.ltd?.benefitActive === 1
                    || data.coverage.majorMedical?.benefitActive === 1
                    || data.coverage.outOfCountry?.benefitActive === 1
                    || data.coverage.paramedicals?.benefitActive === 1
                    || data.coverage.std?.benefitActive === 1
                    || data.coverage.vision?.benefitActive === 1
                    || data.coverage.virtualHealthcare?.benefitActive === 1);

                const noClaims = data.claims.length === 0;
                const isSurvivorBenefit = data.meta.isDependent === false;

                if (isSurvivorBenefit) {
                    dispatch({
                        type: types.GET_DEPENDENT,
                        meta: data.meta,
                        dependents: data.dependents,
                        beneficiaries: data.beneficiaries,
                        trustee: data.trustee,
                        notifications: data.notifications || [],
                        planDesign: data.planDesign,
                        onlySpendingAccountActive,
                        noClaims
                    });

                    dispatch({
                        type: invoiceTypes.GET_ALL_INVOICES,
                        invoices: data.invoices
                    });
                } else {
                    dispatch({
                        type: types.GET_DEPENDENT,
                        meta: data.meta,
                        onlySpendingAccountActive,
                        noClaims
                    });
                }

                dispatch({
                    type: coverageTypes.GET_COVERAGE,
                    coverage: data.coverage
                });

                dispatch({
                    type: usageTypes.GET_USAGE,
                    usage: data.usage
                });

                dispatch({
                    type: claimsTypes.GET_CLAIMS,
                    claims: data.claims
                });

            })
            .then(() => {
                setLoader(dispatch, false);
                return true;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
            });
    };
}

/**
 * @param username
 * @param password
 */
export function login(username, password, language) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return user
            .login({
                username,
                password,
                language
            })
            .then((data) => {
                session.setToken(data.token);

                if (data.dependentID) {
                    session.setIsDependent();
                    events.record('dependent-login', { dependentID: data.dependentID });
                } else if (data.userID) {
                    events.record('login', { userID: data.userID });
                }

                setLoader(dispatch, false);

                window.location.href = '/';
                return true;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
            });
    };
}

/**
 * @param email
 */
export function forgotPassword(email) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return user
            .sendForgotPasswordEmail(email)
            .then(() => {
                dispatch(notifier.displayMessage(i18n.t(tokens.labels.notifications.checkInboxForPasswordRecoveryEmail)));
                setLoader(dispatch, false);
                return true;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
            });
    };
}

/**
 * @param password
 * @param token
 */
export function resetPassword(password, token) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return user
            .resetPassword(password, token)
            .then(() => {
                setLoader(dispatch, false);
                return true;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
            });
    };
}

/**
 * @param email
 */
export function supportEmail(body) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return user
            .sendSupportEmail(body)
            .then(() => {
                dispatch(notifier.displayMessage(i18n.t(tokens.labels.notifications.checkEmailInboxForSupportResponse)));
                setLoader(dispatch, false);

                events.record('send_support_email');
                return true;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
            });
    };
}

/**
 *
 * @param {*} dependent
 */
export function addDependent(dependent) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return user
            .addDependent(dependent)
            .then((data) => {
                dispatch({
                    type: types.ADD_DEPENDENT_SUCCESS,
                    dependent: data
                });

                setLoader(dispatch, false);
                dispatch(notifier.displayMessage(i18n.t(tokens.labels.notifications.dependentAddedSuccessfully)));
                return true;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
                return false;
            });
    };
}

/**
 *
 * @param {*} data
 */
export function updateUserSettings(data, displayNotification = true) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return user
            .updateUserSettings(data)
            .then(() => {
                displayNotification && dispatch(notifier.displayMessage(i18n.t(tokens.labels.notifications.successfullyUpdatedUserSettings)));

                dispatch({
                    type: types.UPDATE_USER_SETTINGS_SUCCESS,
                    data
                });

                setLoader(dispatch, false);
                return true;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
                return false;
            });
    };
}

/**
 * @param data
 * @returns {function(*): Promise<boolean>}
 */
export function saveBankingDetails(data) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return user
            .saveBankingDetails(data)
            .then(() => {
                dispatch({
                    type: types.BANKING_DETAILS_SUCCESS
                });

                setLoader(dispatch, false);
                dispatch(notifier.displayMessage(i18n.t(tokens.labels.notifications.successfullySavedBankingDetails)));
                return true;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
                return false;
            });
    };
}

/**
 * @param userID
 * @returns {function(*): Promise<unknown | boolean>}
 */
export function getBankingDetails(userID) {
    return (dispatch) => {
        setLoader(dispatch, true);
        return user
            .getBankingDetails(userID)
            .then((data) => {
                setLoader(dispatch, false);
                return data;

            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
                return false;
            });
    };
}

/**
 * @param existing
 * @param password
 * @param isDependent
 * @returns {function(*=): Promise<boolean>}
 */
export function changeUserPassword(existing, password) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return user
            .changePassword(existing, password)
            .then(() => {
                setLoader(dispatch, false);
                dispatch(notifier.displayMessage(i18n.t(tokens.labels.notifications.passwordChangedSuccessfully)));

                return true;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
                return false;
            });
    };
}

/**
 *
 * @param {*} dependentID
 * @param {*} dependent
 */
export function editDependent(dependentID, dependent) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return user
            .editDependent(dependentID, dependent)
            .then((data) => {
                dispatch({
                    type: types.UPDATE_DEPENDENT_SUCCESS,
                    dependent: data,
                    dependentID
                });

                setLoader(dispatch, false);
                dispatch(notifier.displayMessage(i18n.t(tokens.labels.notifications.dependentUpdatedSuccessfully)));
                return true;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
                return false;
            });
    };
}

/**
 * @param dependentID
 * @param password
 * @param signage
 * @returns {function(*=): Promise<boolean | never>}
 */
export function removeDependent(dependentID, password, signage) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return user
            .removeDependent(dependentID, password, signage)
            .then(() => {
                dispatch({
                    type: types.REMOVE_DEPENDENT_SUCCESS,
                    dependentID
                });

                setLoader(dispatch, false);
                dispatch(notifier.displayMessage(i18n.t(tokens.labels.notifications.dependentRemovedSuccessfully)));
                return true;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
                return false;
            });
    };
}

/**
 * @param beneficiary
 * @param currentBeneficiaries
 * @param isContingent
 * @returns {function(*=): Promise<boolean>}
 */
export function addBeneficiary(beneficiary, currentBeneficiaries, isContingent) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return user
            .addBeneficiary(beneficiary, currentBeneficiaries)
            .then((data) => {
                dispatch({
                    type: types.ADD_BENEFICIARY_SUCCESS,
                    beneficiary: data,
                    isContingent
                });

                dispatch({
                    type: types.UPDATE_CURRENT_BENEFICIARIES,
                    currentBeneficiaries,
                    isContingent
                });

                setLoader(dispatch, false);
                dispatch(notifier.displayMessage(i18n.t(tokens.labels.notifications.beneficiaryAddedSuccessfully)));
                return true;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
                return false;
            });
    };
}

/**
 * @param beneficiaryID
 * @param beneficiary
 * @param currentBeneficiaries
 * @param isContingent
 * @returns {function(*=): Promise<boolean>}
 */
export function editBeneficiary(beneficiaryID, beneficiary, currentBeneficiaries, isContingent = false) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return user
            .editBeneficiary(beneficiaryID, beneficiary, currentBeneficiaries)
            .then((data) => {
                dispatch({
                    type: types.UPDATE_BENEFICIARY_SUCCESS,
                    beneficiary: data,
                    beneficiaryID,
                    isContingent
                });

                dispatch({
                    type: types.UPDATE_CURRENT_BENEFICIARIES,
                    currentBeneficiaries,
                    isContingent
                });

                setLoader(dispatch, false);
                dispatch(notifier.displayMessage(i18n.t(tokens.labels.notifications.beneficiaryUpdatedSuccessfully)));
                return true;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
                return false;
            });
    };
}

/**
 * @param beneficiaryID
 * @param password
 * @param currentBeneficiaries
 * @param signage
 * @param isContingent
 * @returns {function(*=): Promise<boolean>}
 */
export function removeBeneficiary(beneficiaryID, password, currentBeneficiaries, signage, isContingent = false) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return user
            .removeBeneficiary(beneficiaryID, password, currentBeneficiaries, signage)
            .then(() => {
                dispatch({
                    type: types.REMOVE_BENEFICIARY_SUCCESS,
                    beneficiaryID,
                    isContingent
                });

                dispatch({
                    type: types.UPDATE_CURRENT_BENEFICIARIES,
                    currentBeneficiaries,
                    isContingent
                });

                setLoader(dispatch, false);
                dispatch(notifier.displayMessage(i18n.t(tokens.labels.notifications.beneficiaryRemovedSuccessfully)));
                return true;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
                return false;
            });
    };
}

/**
 *
 * @param {*} trustee
 */
export function addTrustee(trustee) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return user
            .addTrustee(trustee)
            .then((data) => {
                dispatch({
                    type: types.ADD_TRUSTEE_SUCCESS,
                    trustee: data
                });

                setLoader(dispatch, false);
                dispatch(notifier.displayMessage(i18n.t(tokens.labels.notifications.trusteeAddedSuccessfully)));
                return true;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
                return false;
            });
    };
}

/**
 *
 * @param {*} trusteeID
 * @param {*} trustee
 */
export function editTrustee(trusteeID, trustee) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return user
            .editTrustee(trusteeID, trustee)
            .then((data) => {
                dispatch({
                    type: types.UPDATE_TRUSTEE_SUCCESS,
                    trustee: data
                });

                setLoader(dispatch, false);
                dispatch(notifier.displayMessage(i18n.t(tokens.labels.notifications.trusteeUpdatedSuccessfully)));
                return true;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
                return false;
            });
    };
}

/**
 * @param trusteeID
 * @param password
 * @param signage
 * @returns {function(*=): Promise<boolean | never>}
 */
export function removeTrustee(trusteeID, password, signage) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return user
            .removeTrustee(trusteeID, password, signage)
            .then(() => {
                dispatch({
                    type: types.REMOVE_TRUSTEE_SUCCESS,
                    trusteeID
                });

                setLoader(dispatch, false);
                dispatch(notifier.displayMessage(i18n.t(tokens.labels.notifications.trusteeRemovedSucessfully)));
                return true;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
                return false;
            });
    };
}

/**
 * Remove the users JWT and refresh the app.
 */
export function logout() {
    return () => {
        session.removeToken();
        events.record('logout');
        window.location.href = '/login';
        return true;
    };
}

/**
 * @param id
 * @returns {function(*=): Promise<any | never>}
 */
export function removeNotification(id) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return user
            .removeNotification(id)
            .then(() => {
                dispatch({
                    type: types.REMOVE_NOTIFICATION_SUCCESS,
                    id
                });

                setLoader(dispatch, false);
                return true;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
            });
    };
}

/**
 * @returns {function(*=): Promise<boolean>}
 */
export function getUsage(date = null, skipLoader = false) {
    return (dispatch) => {
        if (!skipLoader) {
            setLoader(dispatch, true);
        }

        return user.getUsage(date)
            .then((data) => {
                setLoader(dispatch, false);
                return data;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
            });
    };
}

/**
 * @param id
 * @returns {function(*=): Promise<any | never>}
 */
export function setCurrentSidebarItem(item) {
    return (dispatch) => {
        dispatch({
            type: types.SET_CURRENT_SIDEBAR_ITEM,
            item
        });
    };
}


/**
 * @returns {function(*): Promise<*>}
 */
export function initUpdateCreditCard() {
    return (dispatch) => {
        setLoader(dispatch, true);

        return user
            .initUpdateCreditCard()
            .then((data) => data)
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
            });
    };
}

/**
 * @param checkoutSessionID
 * @returns {function(*): Promise<boolean>}
 */
export function finalizeCreditCardUpdate(checkoutSessionID) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return user
            .finalizeCreditCardUpdate(checkoutSessionID)
            .then(() => true)
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
            });
    };
}

/**
 * @param ComponentName
 * @param downloadParams
 * @returns {function(*): Promise<*>}
 */
export function downloadPDF(ComponentName, downloadParams) {    
    return (dispatch) => {
        setLoader(dispatch, true);        
        return user.downloadPDF(ComponentName, downloadParams)
            .then((data) => {
                setLoader(dispatch, false);
                return data;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
            });
    };
}

/**
 * @param productID
 * @param password
 * @param signage
 * @returns {function(*): Promise<*>}
 */
export function refuseMarketplaceProduct(productID, password, signage) {
    return (dispatch) => {
        setLoader(dispatch, true);
        return user.refuseMarketplaceProduct(productID, password, signage)
            .then((data) => {
                setLoader(dispatch, false);
                return data;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
            });
    };
}

/**
 *
 * @param institutionNum
 * @param transitNum
 * @returns {function(*): Promise<*>}
 */
export function validateBankingDetails(institutionNum, transitNum) {
    return (dispatch) => {
        setBankingDetailsVerifying(dispatch, true);

        return user.validateBankingDetails(institutionNum, transitNum)
            .then((data) => {
                setBankingDetailsVerifying(dispatch, false);
                return data;
            })
            .catch((error) => {
                setBankingDetailsVerifying(dispatch, false);
                dispatch(notifier.displayError(error));
                return error;
            });
    };
}